<template>
  <div :id="select_root_id" class="select-root" :style="getRootStyle">
    <div :class="['form-label label-text', { 'label-text-filter': small }]" v-if="label">
      {{ $t(label) }}
      <span v-if="is_required" class="req-sign">*</span>
    </div>

    <div class="form-control disabled" :style="cus_style" v-if="disabled || readOnly">
      {{ getDisplayText() }}
    </div>
    <validation-provider
      v-else
      :name="name"
      :rules="rules"
      v-slot="{ errors }"
      v-click-outside.stop="hideOptionsCustom"
    >
      <div
        :class="['dropdown', { closeDropDown: !show_options }, { openDropDown: show_options }]"
        @click="removeOption"
      >
        <b-input
          :class="[
            'select',
            'position-relative',
            { 'sidebar-btn': small },
            {
              'invalid-select': is_invalid || (errors && errors.length) ? true : false,
            },
          ]"
          :readonly="true"
          lazy-formatter
          :label="label"
          :disabled="disabled ? true : false"
          :placeholder="
            $t(placeholder) ||
            $t(placeholderText) ||
            (filteredOptions.length ? $t('select-an-option') : $t('no-options-available'))
          "
          v-model.trim="inputfield.value"
          :style="cus_style"
          :small="small"
          :id="id"
          :ref="cus_ref"
        />
        <div
          v-show="!inputfield.text || multi_select"
          :class="[
            'position-absolute',
            'input-text-search',
            {
              'input-text-custom-disabled pointer-events-none': disabled,
            },
          ]"
          @click="toggleOptionsCustom"
        >
          <base-input
            placeholder="search-here"
            :remove_default_margin="true"
            type="text"
            :id="`${name}-${id}`"
            field="search_term"
            :value="search_term"
            @change-value="setValue"
            :disabled="disabled ? true : false"
            :is_invalid="is_invalid || (errors && errors.length) ? true : false"
            ref="searchTerm"
          />
        </div>
        <div
          v-if="inputfield.text && !multi_select"
          :id="inputTextCustomId"
          :class="[
            'position-absolute',
            'input-text-custom',
            {
              'input-text-custom-disabled pointer-events-none': disabled,
            },
          ]"
        >
          {{ useI18n ? $t(inputfield.text) : inputfield.text }}
        </div>
      </div>

      <div class="options" v-if="show_options" :style="`width:${optionListWidth}`">
        <div class="list">
          <div
            :class="[
              'option',
              { selected: inputfield?.value === option?.value },
              { active: index === activeIndex },
            ]"
            v-for="(option, index) in filteredOptions"
            :key="option.value"
            @click.stop="selectOption(option)"
            @mouseenter="activeIndex = -1"
          >
            {{ useI18n ? $t(option.text) : option.text }}
          </div>
          <div class="option" v-if="!filteredOptions.length">
            {{ $t('no-options-available') }}
          </div>
        </div>
      </div>

      <div class="helper-text" :style="helper_text_style" v-if="helper_text">
        {{ $t(helper_text) }}
      </div>
      <div class="error-text mt-2" :style="helper_text_style" v-if="errors && errors.length">
        {{ errors[0] }}
      </div>
      <slot name="attachment"></slot>
    </validation-provider>
  </div>
</template>

<script>
import { nanoid } from 'nanoid'

export default {
  emits: ['change-value', 'change', 'search'],
  props: [
    'label',
    'placeholder',
    'is_invalid',
    'helper_text',
    'helper_text_style',
    'field',
    'value',
    'cus_style',
    'remove_default_margin',
    'readOnly',
    'disabled',
    'cus_ref',
    'is_required',
    'options',
    'small',
    'type',
    'rules',
    'name',
    'id',
    'useI18n',
    'multi_select',
    'remoteSearch',
  ],
  data() {
    return {
      inputfield: { text: null, value: null },
      show_options: false,
      search_term: null,
      filteredOptions: [],
      select_root_id: `select_root_${new Date().getTime()}`,
      inputTextCustomId: nanoid(),
      activeIndex: -1,
      keydownListenerAdded: false,
    }
  },
  methods: {
    setValue(val, field) {
      this[field] = val
    },
    toggleOptionsCustom() {
      if (this.search_term && !this.multi_select) return
      if (!this.disabled) {
        this.show_options = !this.show_options
        this.activeIndex = -1
      }
    },
    toggleOptions() {
      this.show_options = !this.show_options
    },
    hideOptions() {
      this.show_options = false
    },
    hideOptionsCustom(e) {
      if (e.target.id !== this.inputTextCustomId) {
        this.show_options = false
      }
    },
    removeOption() {
      if (this.disabled) return
      if (this.inputfield.text) {
        if (this.useI18n) {
          this.search_term = this.$t(this.inputfield.text)
        } else {
          this.search_term = this.inputfield.text
        }
        this.inputfield = { text: null, value: null }
        this.activeIndex = -1
        this.search_term = null
        this.toggleOptions()
      }
      this.$nextTick(() => this.$refs?.searchTerm?.$el?.querySelector('input')?.focus())
    },
    selectOption(option) {
      if (this.inputfield?.value !== option?.value) this.inputfield = option
      else this.inputfield = { text: null, value: null }
      this.search_term = this.inputfield.text
      if (!this.multi_select) {
        this.hideOptions()
      }
    },
    setInputfield() {
      if (this.value && this.filteredOptions && this.filteredOptions.length)
        this.inputfield = this.filteredOptions?.find((val) => val.value === this.value)
      else this.inputfield = { text: null, value: null }
    },
    // Method to handle keyboard events
    handleKeydown(event) {
      if (!this.show_options) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (this.activeIndex < this.filteredOptions.length - 1) {
          this.activeIndex++
        } else {
          this.activeIndex = 0
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (this.activeIndex > 0) {
          this.activeIndex--
        } else {
          this.activeIndex = this.filteredOptions.length - 1
        }
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (this.activeIndex >= 0 && this.activeIndex < this.filteredOptions.length) {
          this.selectOption(this.filteredOptions[this.activeIndex])
        }
      }
      // Ensure the active option is scrolled into view
      this.$nextTick(() => {
        const optionElements = this.$el.querySelectorAll('.option')
        if (optionElements[this.activeIndex]) {
          optionElements[this.activeIndex].scrollIntoView({
            block: 'nearest', // Adjusts the scrolling position
          })
        }
      })
    },
    getDisplayText() {
      const text = this.useI18n ? this.$t(this.inputfield?.text) : this.inputfield?.text
      return (
        text ||
        this.$t(this.placeholder) ||
        this.$t(this.placeholderText) ||
        this.$t(this.filteredOptions.length ? 'select-an-option' : 'no-options-available')
      )
    },
  },
  watch: {
    value() {
      this.setInputfield()
    },
    show_options(val) {
      if (!val) this.search_term = null
    },
    search_term(val) {
      if (this.remoteSearch) {
        if (val) this.show_options = true
        this.$emit('search', val)
        return
      }
      if (val) {
        this.show_options = true
        this.filteredOptions = this.options.filter((each) => {
          if (!isNaN(each.text)) {
            return each && each.value && each.text && `${each.text}`.includes(val)
          }

          return (
            each &&
            each.value &&
            each.text &&
            this.$t(each.text).toLocaleLowerCase().includes(val?.toLocaleLowerCase())
          )
        })
      } else {
        this.filteredOptions = this.options.filter((each) => each.value)
      }
    },
    options() {
      this.filteredOptions = this.options.filter((each) => each.value)
    },
    inputfield(value) {
      this.$emit(
        'change-value',
        value && this.filteredOptions && this.filteredOptions.length
          ? this.filteredOptions?.find((rec) => rec.value === value.value)?.value
          : null,
        this.field
      )
      this.$emit('change')
    },
  },
  computed: {
    getRootStyle() {
      return `margin-bottom: ${this.remove_default_margin ? '0px' : '20px'};position:relative;`
    },
    placeholderText() {
      return this.options?.find((each) => !each.value)?.text
    },
    optionListWidth() {
      return (
        document.getElementById(this.select_root_id).getElementsByTagName('input')[0]?.offsetWidth +
        'px'
      )
    },
  },
  mounted() {
    this.filteredOptions = this.options.filter((each) => each.value)
    this.setInputfield()

    // Listen for keydown events when the dropdown is open
    // Prevent duplicate listeners
    if (!this.keydownListenerAdded) {
      document.addEventListener('keydown', this.handleKeydown)
      this.keydownListenerAdded = true
    }
  },
  beforeDestroy() {
    // Clean up the event listener when the component is destroyed
    document.removeEventListener('keydown', this.handleKeydown)
    this.keydownListenerAdded = false
  },
}
</script>

<style lang="scss" scoped>
.label-text {
  font-size: 14px;
  color: var(--color-61);
  margin-bottom: 5px;
  font-weight: 700;
}
// .label-text-filter {
//   margin-bottom: 5px;
//   font-weight: 400;
//   font-size: 12px;
//   line-height: 16px;
//   letter-spacing: 0.4px;
//   color: var(--color-76);
// }

.invalid-select {
  border-color: var(--color-47) !important;
  background-color: var(--color-01) !important;
}

.cross-prefix {
  position: absolute;
  top: 6px;
  inset-inline-end: 30px;
  img {
    cursor: pointer;
  }
}

.dropdown {
  position: relative;
}

.closeDropDown:before {
  content: url('@/assets/images/icons/arrow-icon.svg');
  position: absolute;
  transform: rotate(270deg);
  inset-inline-end: 10px;
  top: 8px;
  z-index: 1;
}

.openDropDown:before {
  content: url('@/assets/images/icons/arrow-icon.svg');
  position: absolute;
  transform: rotate(90deg);
  inset-inline-end: 10px;
  top: 8px;
  z-index: 1;
}

.select {
  height: 40px;
  font-size: 14px;
  padding: 10px;
  background: var(--color-01);
  color: var(--color-61);
  border: 1px solid;
  border-radius: 6px;
  border-color: var(--color-64);
  box-shadow: 0px 1px 2px var(--color-37);
  font-weight: 400;

  &:focus-visible {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:hover {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:focus {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:disabled {
    background-color: var(--color-67);
  }
}
.sidebar-btn {
  padding: 6px 10px;
  height: 36px;
  border: 1px solid var(--color-04);
  border-radius: 4px;
}

.options {
  position: absolute;
  z-index: 21;
  background-color: var(--color-01);
  border: 1px solid;
  border-color: var(--color-04);
  box-shadow: 0px 1px 2px var(--color-37);
  width: 100%;
  font-weight: 400;
  .list {
    max-height: 200px;
    width: 100%;
    overflow: auto;
    .option {
      cursor: pointer;
      padding: 0.5rem;
      font-size: 14px;
      color: var(--color-17);
      &:hover,
      &.active {
        background: var(--color-23);
        color: var(--color-01);
      }
      &.selected {
        background: var(--color-23);
        color: var(--color-01);
      }
    }
  }
}

.options .list::-webkit-scrollbar {
  width: 5px;
}

.options .list::-webkit-scrollbar-track {
  background: var(--color-44);
}

.options .list::-webkit-scrollbar-thumb {
  background: var(--color-23);
}

.options .list::-webkit-scrollbar-thumb:hover {
  background: var(--color-45);
}

.input-text-custom {
  top: 9px;
  inset-inline-start: 10px;
  background: white;
  width: calc(100% - 20px);
  font-size: 14px;
  color: var(--color-61);
}

.input-text-custom-disabled {
  background-color: var(--color-67);
}

.input-text-search {
  top: 0;
  inset-inline-start: 0;
  background: white;
  width: calc(100%);
  font-size: 14px;
  color: var(--color-61);
}
</style>